import React from "react";
import {
  BarChart3, AlertTriangle, ShieldCheck, CalendarRange,
  AlertCircle, Workflow, Server, Files, FileText, PanelLeft,
} from "lucide-react";
import { cn } from "../utils";
import { shell, typography } from "../tokens";
import { HeaderIconButton } from "./app-header";

export const RISK_CENTER_TABS = [
  { id: "dashboards",    labelKey: "riskCenter.tabs.dashboard", icon: BarChart3 },
  { id: "risk",          labelKey: "riskCenter.tabs.risk",      icon: AlertTriangle },
  { id: "controls",      labelKey: "riskCenter.tabs.controls",  icon: ShieldCheck },
  { id: "actions",       labelKey: "riskCenter.tabs.actions",   icon: CalendarRange },
  { id: "incidents",     labelKey: "riskCenter.tabs.incidents", icon: AlertCircle },
  { id: "workflows",     labelKey: "riskCenter.tabs.workflows", icon: Workflow },
  { id: "inventory",     labelKey: "riskCenter.tabs.assets",    icon: Server },
  { id: "files",         labelKey: "riskCenter.tabs.files",     icon: Files },
  { id: "create_report", labelKey: "riskCenter.tabs.reports",   icon: FileText },
];

function getInitials(name) {
  if (!name || name === "N/A") return "NA";
  return name
    .split(" ")
    .filter((w) => w.toLowerCase() !== "and")
    .map((w) => w.charAt(0).toUpperCase())
    .slice(0, 2)
    .join("");
}

export function ModuleContextBadge({ name, color, companyName }) {
  return (
    <div className="flex items-center gap-2.5 px-4 py-2 border-r border-gray-200 flex-shrink-0 min-w-0">
      <div
        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: color || "#6366f1" }}
        title={name}
      >
        <span className="text-white font-bold" style={{ fontSize: "0.62rem" }}>
          {getInitials(name)}
        </span>
      </div>
      <div className="flex flex-col leading-tight min-w-0">
        <span className="text-[13px] font-semibold text-gray-900 truncate">
          {name}
        </span>
        {companyName && (
          <span className="text-[11px] text-gray-400 truncate">
            {companyName}
          </span>
        )}
      </div>
    </div>
  );
}

export function ModuleTabBar({ tabs, value, onChange, leading, expandButton, className }) {
  return (
    <div
      className={cn(
        "flex w-full h-14 bg-white border-b flex-shrink-0 relative z-[30]",
        shell.border,
        className
      )}
    >
      {expandButton}
      {leading}
      <nav className="flex flex-1 min-w-0 overflow-x-auto scrollbar-hide items-stretch">
        {tabs.map(({ id, label, icon: Icon }) => {
          const active = value === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => onChange(id)}
              className={cn(
                "flex items-center gap-2 px-4 whitespace-nowrap border-b-2 transition-colors flex-shrink-0",
                "text-[13px] font-medium leading-none",
                active
                  ? cn(shell.activeText, "border-blue-600", shell.activeBg)
                  : cn(shell.idleText, "border-transparent hover:text-gray-700", shell.hoverBg)
              )}
            >
              {Icon && (
                <Icon
                  className="w-4 h-4 flex-shrink-0"
                  strokeWidth={2}
                  aria-hidden="true"
                />
              )}
              <span>{label}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}

export function ModuleTabExpandButton({ onClick, title }) {
  return (
    <div className="flex items-center pl-3 pr-1 border-r border-gray-100 flex-shrink-0">
      <HeaderIconButton onClick={onClick} title={title} aria-label={title}>
        <PanelLeft className="w-4 h-4" />
      </HeaderIconButton>
    </div>
  );
}

export function SecondaryNavPanel({
  title,
  collapsed,
  onToggle,
  toggleTitle,
  headerActions,
  children,
  className,
}) {
  return (
    <aside
      className={cn(
        "flex flex-col flex-shrink-0 overflow-hidden transition-all duration-300 border-r",
        shell.bg,
        shell.border,
        collapsed ? "w-0 min-w-0" : "w-[218px] min-w-[218px]",
        "max-md:fixed max-md:z-50 max-md:top-16 max-md:h-[calc(100dvh-4rem)] max-md:shadow-xl",
        collapsed ? "max-md:hidden" : "max-md:flex",
        "md:relative md:top-auto md:h-full md:shadow-none",
        className
      )}
    >
      {!collapsed && (
        <>
          <div className={cn("flex items-center h-14 px-4 border-b flex-shrink-0", shell.border)}>
            <span className="text-gray-900 flex-1 truncate" style={{ ...typography.bodyBold, fontSize: "0.85rem" }}>
              {title}
            </span>
            {headerActions}
            {onToggle && (
              <HeaderIconButton onClick={onToggle} title={toggleTitle} aria-label={toggleTitle} className="ml-1">
                <PanelLeft className="w-4 h-4 rotate-180" />
              </HeaderIconButton>
            )}
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto">{children}</div>
        </>
      )}
    </aside>
  );
}
